import React, { useEffect, useState, useCallback } from 'react';
import { Tour, TourProps } from 'antd';
import { useLocation } from 'react-router-dom';
import './WebsiteTour.pcss';

interface WebsiteTourProps {
    open: boolean;
    onClose: () => void;
    current: number;
    onChange: (current: number) => void;
}

export const WebsiteTour: React.FC<WebsiteTourProps> = ({ open, onClose, current, onChange }) => {
    const location = useLocation();
    const [isNavigating, setIsNavigating] = useState(false);

    // Only show general header tour on homepage (path = "/")
    const shouldShowHeaderTour = location.pathname === '/';
    const steps: TourProps['steps'] = shouldShowHeaderTour ? [
        {
            title: 'Welcome to Our Trading Platform! 🎉',
            description: 'This is your main navigation header where you can access all platform features. Let\'s explore what each section offers.',
            target: () => document.querySelector('.pg__header') as HTMLElement,
        },
        {
            title: 'Trading Section 📈',
            description: 'Access our comprehensive trading features here. This is where you can trade stocks with real-time market data.',
            target: () => document.querySelector('[data-tour="trading-menu"]') as HTMLElement,
        },
        {
            title: 'Stock Trading 📊',
            description: 'Click here to view live stock prices, order books, market depth, and place your trades. Perfect for active trading!',
            target: () => document.querySelector('[data-tour="trading-stock"]') as HTMLElement,
        },
        {
            title: 'Your Profile 👤',
            description: 'Access your personal dashboard, view your assets, manage account settings, and check notifications.',
            target: () => document.querySelector('[data-tour="user-profile"]') as HTMLElement,
        },
    ] : [];

    // Add page-specific steps based on current route
    const getPageSpecificSteps = () => {
        const pageSteps: TourProps['steps'] = [];
        
                    if (location.pathname.includes('/priceboard')) {
                pageSteps.push({
                    title: 'Welcome to Trading Dashboard! 📈',
                    description: 'This is your comprehensive trading workspace. Here you can analyze stocks, view real-time prices, and execute trades efficiently.',
                    target: () => document.querySelector('[data-tour="price-board-table"]') as HTMLElement,
                });
                pageSteps.push({
                    title: 'Stock Search 🔍',
                    description: 'Search for specific stocks by ticker symbol or company name to quickly find what you\'re looking for.',
                    target: () => document.querySelector('[data-tour="stock-search-input"]') as HTMLElement,
                });
                pageSteps.push({
                    title: 'Stock Group Filters 📊',
                    description: 'Filter stocks by market groups like VN30, HNX30, VN100 to focus on specific market segments.',
                    target: () => document.querySelector('[data-tour="stock-group-filter"]') as HTMLElement,
                });
                pageSteps.push({
                    title: 'Price Board Table 📋',
                    description: 'This is the main trading table showing real-time stock prices, volumes, bid/ask data, and market analysis. Click on any ticker to start trading!',
                    target: () => document.querySelector('[data-tour="price-board-table"]') as HTMLElement,
                });
                pageSteps.push({
                    title: 'Create Order 💰',
                    description: 'This is the Create Order button where you can start placing your buy or sell orders. Click this to open the trading modal.',
                    target: () => document.querySelector('[data-tour="create-order-btn"]') as HTMLElement,
                });
                pageSteps.push({
                    title: 'Trading Modal Opens 🎯',
                    description: 'The trading modal will now open automatically to continue the tour inside the comprehensive trading interface.',
                    target: () => document.querySelector('[data-tour="create-order-btn"]') as HTMLElement,
                });

                // Buy Order Modal tour steps (simplified - only essential steps)
                pageSteps.push({
                    title: 'History Navigation 📂',
                    description: 'Switch between Orders (your trading history), Orders Match (executed trades), Portfolio (current holdings), and Asset (account summary) tabs.',
                    target: () => document.querySelector('[data-tour="buy-modal-history-tabs"]') as HTMLElement,
                });
                pageSteps.push({
                    title: 'Order Details Table 📋',
                    description: 'Detailed view of all your orders showing symbol, buy/sell direction, order type, quantities, prices, status, and available actions like cancellation.',
                    target: () => document.querySelector('[data-tour="buy-modal-orders-table"]') as HTMLElement,
                });
                
                // Orders Match Tab Tour
                pageSteps.push({
                    title: 'Orders Match Tab 🔄',
                    description: 'Now let\'s explore the Orders Match tab to see your executed trades. The tour will automatically switch to this tab.',
                    target: () => document.querySelector('[data-tour="buy-modal-history-tabs"]') as HTMLElement,
                });
                pageSteps.push({
                    title: 'Matched Orders Table 📋',
                    description: 'View detailed information about your executed trades including symbol, amount, price, side, creation time, and settlement status.',
                    target: () => document.querySelector('.price-board__buy-modal__history__body__table') as HTMLElement,
                });
                
                // Portfolio Tab Tour
                pageSteps.push({
                    title: 'Portfolio Tab 💼',
                    description: 'Next, let\'s check your Portfolio tab to see your current holdings. The tour will automatically switch to this tab.',
                    target: () => document.querySelector('[data-tour="buy-modal-history-tabs"]') as HTMLElement,
                });
                pageSteps.push({
                    title: 'Portfolio Summary 💰',
                    description: 'View your total portfolio value and overall investment performance in this summary section.',
                    target: () => document.querySelector('[data-tour="buy-modal-portfolio-summary"]') as HTMLElement,
                });
                pageSteps.push({
                    title: 'Portfolio Holdings 📈',
                    description: 'Detailed view of all your stock holdings showing symbol, available volume, total volume, and last update time.',
                    target: () => document.querySelector('.price-board__buy-modal__history__body__table') as HTMLElement,
                });
                
                // Asset Tab Tour
                pageSteps.push({
                    title: 'Asset Tab 🏦',
                    description: 'Finally, let\'s explore your Asset tab to see your account summary. The tour will automatically switch to this tab.',
                    target: () => document.querySelector('[data-tour="buy-modal-history-tabs"]') as HTMLElement,
                });
                pageSteps.push({
                    title: 'Account Assets 💳',
                    description: 'Complete overview of your account including total assets, cash balance, securities value, and other important financial information.',
                    target: () => document.querySelector('[data-tour="buy-modal-history"]') as HTMLElement,
                });
            }

        // Profile pages tours - Start on /profile/assets but continue on any profile path during tour
        if (location.pathname === '/profile/assets' || 
            (location.pathname.startsWith('/profile/') && open)) { // Allow continuation if tour is open
            
            // Portfolio Value step - target the assets-card or stats-card
            pageSteps.push({
                title: 'Portfolio Value 📊',
                description: 'View your available balance for trading and portfolio management.',
                target: () => location.pathname === '/profile/assets' ? 
                    (document.querySelector('.assets-card') as HTMLElement || 
                     document.querySelector('.stats-card') as HTMLElement) : 
                    null,
            });
            
            pageSteps.push({
                title: 'Cash Balance 💰',
                description: 'View your available cash balance in VND for making new trades and investments.',
                target: () => location.pathname === '/profile/assets' ? 
                    document.querySelector('[data-tour="assets-cash-balance"]') as HTMLElement : 
                    null,
            });
            
            pageSteps.push({
                title: 'Order Management Navigation 📊',
                description: 'Navigate to Order Management to view and manage your trading orders.',
                target: () => document.querySelector('[data-tour="profile-orders-menu"]') as HTMLElement,
            });
            
            // Add steps for /profile/orders page features
            if (location.pathname === '/profile/orders' || open) {
                pageSteps.push({
                    title: 'Total Assets Value 💰',
                    description: 'View your total assets value including all stock positions, cash balance, and P&L information.',
                    target: () => document.querySelector('[data-tour="orders-total-assets"]') as HTMLElement,
                });
                pageSteps.push({
                    title: 'Order Management Tabs 📊',
                    description: 'Switch between different order views: Inventory (your holdings), Order Stock (pending orders), and Order Match (completed trades).',
                    target: () => document.querySelector('[data-tour="orders-menu-tabs"]') as HTMLElement,
                });
                pageSteps.push({
                    title: 'Inventory Tab 📦',
                    description: 'View your current stock holdings and portfolio positions.',
                    target: () => document.querySelector('[data-tour="inventory-tab"]') as HTMLElement,
                });
                pageSteps.push({
                    title: 'Inventory Content 📊',
                    description: 'This table shows all your current stock holdings, including quantity, current value, and profit/loss for each position.',
                    target: () => document.querySelector('[data-tour="orders-tab-content"]') as HTMLElement,
                });
                pageSteps.push({
                    title: 'Order Stock Tab 📋',
                    description: 'Monitor your pending buy and sell orders that are waiting to be executed.',
                    target: () => document.querySelector('[data-tour="order-stock-tab"]') as HTMLElement,
                });
                pageSteps.push({
                    title: 'Order Stock Content 📋',
                    description: 'View all your pending orders with details like stock symbol, order type, quantity, price, and status.',
                    target: () => document.querySelector('[data-tour="orders-tab-content"]') as HTMLElement,
                });
                pageSteps.push({
                    title: 'Order Match Tab ✅',
                    description: 'Review your completed trades and transaction history.',
                    target: () => document.querySelector('[data-tour="order-match-tab"]') as HTMLElement,
                });
                pageSteps.push({
                    title: 'Order Match Content ✅',
                    description: 'Complete trading history showing all executed orders with timestamps, prices, and transaction details.',
                    target: () => document.querySelector('[data-tour="orders-tab-content"]') as HTMLElement,
                });
            }
        }
        
        return pageSteps;
    };

    // Combine general steps with page-specific steps
    const allSteps = [...steps, ...getPageSpecificSteps()];

    // Filter steps based on available elements
    // Special handling for modal steps - include them even if modal isn't open yet
    const availableSteps = allSteps.filter(step => {
        const target = typeof step.target === 'function' ? step.target() : step.target;
        
        // Check if this is a modal step by examining the target function
        const isModalStep = step.target && typeof step.target === 'function' && 
                           step.target.toString().includes('buy-modal');
        
        // Check if this is a profile navigation step
        const isProfileNavStep = step.title && typeof step.title === 'string' && 
                                step.title.includes('Order Management Navigation');
        
        // Check if this is the Portfolio Value or Cash Balance step
        const isPortfolioStep = step.title && typeof step.title === 'string' && 
                               (step.title.includes('Portfolio Value') || step.title.includes('Cash Balance'));
        
        // Check if this is an asset detail step
        const isAssetDetailStep = step.title && typeof step.title === 'string' && 
                                 (step.title.includes('Account Assets') || 
                                  step.title.includes('Asset Tab'));
        
        // Check if this is an orders page step
        const isOrdersPageStep = step.title && typeof step.title === 'string' && 
                                 (step.title.includes('Total Assets Value') || 
                                  step.title.includes('Order Management Tabs') || 
                                  step.title.includes('Inventory Tab') || 
                                  step.title.includes('Inventory Content') ||
                                  step.title.includes('Order Stock Tab') || 
                                  step.title.includes('Order Stock Content') ||
                                  step.title.includes('Order Match Tab') ||
                                  step.title.includes('Order Match Content'));
                                  
        // For orders page steps, always include them (we handle null targets in the step logic)
        if (isOrdersPageStep) {
            return true;
        }
        
        // For modal steps and profile navigation steps, always include them in the tour flow
        if (isModalStep || isProfileNavStep) {
            return true;
        }
        
        // For Portfolio Value step and asset detail steps, include them even if target is null (we handle this in the step logic)
        if (isPortfolioStep || isAssetDetailStep) {
            return true;
        }
        
        // For non-modal steps, only include if target exists
        return target && document.contains(target);
    });

    // Debug logging for tour steps
    useEffect(() => {
        if (open) {
            console.log('🎯 Tour Debug - Path:', location.pathname);
            console.log('🎯 Tour Debug - Available steps count:', availableSteps.length);
            console.log('🎯 Tour Debug - Current step:', current);
            console.log('🎯 Tour Debug - Steps:', availableSteps.map(s => s.title));
            if (current >= availableSteps.length) {
                console.log('⚠️ Current step index exceeds available steps! Adjusting...');
                // Adjust current step to valid index
                const validStep = Math.min(current, availableSteps.length - 1);
                if (validStep >= 0) {
                    setTimeout(() => onChange(validStep), 100);
                }
            }
        }
    }, [open, current, availableSteps.length, location.pathname, onChange, availableSteps]);

    // Shared function to handle step advancement with auto-click logic
    const handleStepAdvancement = useCallback((newCurrent: number) => {
        const newStep = availableSteps[newCurrent];
        
        // Check if this is the "Trading Modal Opens" step
        if (newStep && typeof newStep.title === 'string' && newStep.title.includes('Trading Modal Opens')) {
            setTimeout(() => {
                const createOrderBtn = document.querySelector('[data-tour="create-order-btn"]') as HTMLElement;
                if (createOrderBtn) {
                    createOrderBtn.click();
                    
                    setTimeout(() => {
                        const modal = document.querySelector('[data-tour="buy-order-modal"]');
                        if (modal) {
                            onChange(newCurrent + 1);
                        } else {
                            setTimeout(() => onChange(newCurrent + 1), 1000);
                        }
                    }, 500);
                } else {
                    onChange(newCurrent);
                }
            }, 1000);
            return;
        }

        // Check if this is a tab switching step
        if (newStep && typeof newStep.title === 'string' && newStep.title.includes('Orders Match Tab')) {
            setTimeout(() => {
                const tabItems = document.querySelectorAll('.price-board__buy-modal__history__tab__item');
                const ordersMatchTab = Array.from(tabItems).find(tab => 
                    tab.textContent?.trim() === 'Orders Match'
                ) as HTMLElement;
                if (ordersMatchTab) {
                    ordersMatchTab.click();
                    setTimeout(() => onChange(newCurrent + 1), 500);
                } else {
                    onChange(newCurrent);
                }
            }, 1000);
            return;
        }
        
        if (newStep && typeof newStep.title === 'string' && newStep.title.includes('Portfolio Tab')) {
            setTimeout(() => {
                const tabItems = document.querySelectorAll('.price-board__buy-modal__history__tab__item');
                const portfolioTab = Array.from(tabItems).find(tab => 
                    tab.textContent?.trim() === 'Portfolio'
                ) as HTMLElement;
                if (portfolioTab) {
                    portfolioTab.click();
                    setTimeout(() => onChange(newCurrent + 1), 500);
                } else {
                    onChange(newCurrent);
                }
            }, 1000);
            return;
        }
        
        if (newStep && typeof newStep.title === 'string' && newStep.title.includes('Asset Tab')) {
            setTimeout(() => {
                const tabItems = document.querySelectorAll('.price-board__buy-modal__history__tab__item');
                const assetTab = Array.from(tabItems).find(tab => 
                    tab.textContent?.trim() === 'Asset'
                ) as HTMLElement;
                if (assetTab) {
                    assetTab.click();
                    setTimeout(() => onChange(newCurrent + 1), 500);
                } else {
                    onChange(newCurrent);
                }
            }, 1000);
            return;
        }

        // Check if this is Order Management Navigation step
        if (newStep && typeof newStep.title === 'string' && newStep.title.includes('Order Management Navigation')) {
            setIsNavigating(true);
            setTimeout(() => {
                const orderManagementMenu = document.querySelector('[data-tour="profile-orders-menu"]') as HTMLElement;
                if (orderManagementMenu) {
                    orderManagementMenu.click();
                    setTimeout(() => {
                        setIsNavigating(false);
                        onChange(newCurrent);
                    }, 1500);
                } else {
                    setIsNavigating(false);
                    onChange(newCurrent);
                }
            }, 1000);
            return;
        }
        
        if (newStep && typeof newStep.title === 'string') {
            if (newStep.title.includes('Order Stock Tab') && !newStep.title.includes('Content')) {
                setTimeout(() => {
                    const orderStockTab = document.querySelector('[data-tour="order-stock-tab"]') as HTMLElement;
                    if (orderStockTab) {
                        orderStockTab.click();
                    }
                    onChange(newCurrent);
                }, 500);
                return;
            }
            
            if (newStep.title.includes('Order Match Tab') && !newStep.title.includes('Content')) {
                setTimeout(() => {
                    const orderMatchTab = document.querySelector('[data-tour="order-match-tab"]') as HTMLElement;
                    if (orderMatchTab) {
                        orderMatchTab.click();
                    }
                    onChange(newCurrent);
                }, 500);
                return;
            }
        }

        // Check if the target for the new step exists
        if (newStep && newStep.target) {
            const target = typeof newStep.target === 'function' ? newStep.target() : newStep.target;
            
            if (!target || !document.contains(target)) {
                if (newStep.title && typeof newStep.title === 'string' && 
                    (newStep.title.includes('Portfolio Value') || newStep.title.includes('Cash Balance')) && 
                    location.pathname !== '/profile/assets') {
                    setTimeout(() => onChange(newCurrent + 1), 100);
                    return;
                }
                
                if (newStep.title && typeof newStep.title === 'string' && 
                    (newStep.title.includes('Total Assets Value') || 
                     newStep.title.includes('Order Management Tabs') || 
                     newStep.title.includes('Inventory Tab') || 
                     newStep.title.includes('Inventory Content') ||
                     newStep.title.includes('Order Stock Tab') || 
                     newStep.title.includes('Order Stock Content') ||
                     newStep.title.includes('Order Match Tab') ||
                     newStep.title.includes('Order Match Content')) && 
                    location.pathname !== '/profile/orders') {
                    setTimeout(() => onChange(newCurrent + 1), 100);
                    return;
                }
                
                const waitTime = (typeof newStep.title === 'string' && newStep.title.includes('Navigation')) ? 2000 : 500;
                setTimeout(() => {
                    const retryTarget = typeof newStep.target === 'function' ? newStep.target() : newStep.target;
                    if (retryTarget && document.contains(retryTarget)) {
                        onChange(newCurrent);
                    } else if (newCurrent < availableSteps.length - 1) {
                        onChange(newCurrent + 1);
                    } else {
                        onClose();
                    }
                }, waitTime);
                return;
            }
        }

        // Regular step advancement
        onChange(newCurrent);
    }, [availableSteps, onChange, onClose, location.pathname, setIsNavigating]);

    // Keyboard event handler for tour navigation
    useEffect(() => {
        if (!open) return;
        
        const handleKeyDown = (event: KeyboardEvent) => {
            // Prevent default behavior for Enter and Escape
            if (event.key === 'Enter' || event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
            }
            
            if (event.key === 'Enter') {
                // Enter = Next step (or close if last step)
                if (current < availableSteps.length - 1) {
                    handleStepAdvancement(current + 1);
                } else {
                    onClose(); // Close tour if on last step
                }
            } else if (event.key === 'Escape') {
                // Escape = Close tour
                onClose();
            }
        };
        
        // Add event listener
        document.addEventListener('keydown', handleKeyDown, true);
        
        // Cleanup
        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [open, current, availableSteps.length, onChange, onClose, handleStepAdvancement]);

    // Dynamic placement based on current step
    const getCurrentPlacement = () => {
        const currentStepData = availableSteps[current];
        if (currentStepData?.target) {
            const targetElement = typeof currentStepData.target === 'function' ? currentStepData.target() : currentStepData.target;
            if (targetElement) {
                const rect = targetElement.getBoundingClientRect();
                // If target is in the top 20% of viewport, place below
                if (rect.top < window.innerHeight * 0.2) {
                    return 'bottom';
                }
                // If target is in the bottom 20% of viewport, place above
                if (rect.top > window.innerHeight * 0.8) {
                    return 'top';
                }
            }
        }
        return 'bottom';
    };

    const handleTourClose = () => {
        if (isNavigating) {
            setTimeout(() => {
                if (isNavigating) {
                    return;
                } else {
                    onClose();
                }
            }, 2000);
            return;
        }
        onClose();
    };

    return (
        <Tour
            key={location.pathname.startsWith('/profile/') ? 'profile-tour' : `tour-${current}-${location.pathname}`}
            open={open}
            onClose={handleTourClose}
            steps={availableSteps}
            current={current}
            zIndex={10000}
            onChange={handleStepAdvancement}
            type="primary"
            arrow={true}
            placement={getCurrentPlacement()}
            gap={{ offset: 8, radius: 6 }}
            scrollIntoViewOptions={{
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest'
            }}
            mask={{
                style: {
                    boxShadow: 'inset 0 0 15px #333',
                },
            }}
        />
    );
};
